import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, Box, RadioGroup, FormControlLabel, Radio, Alert
} from '@mui/material';

// 1. PROPS INTERFACE FOR THE DIALOG
interface SaveFilterDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (nameOrAction: string, filterId?: string, currentFilterState?: any) => void;
    mode: 'save' | 'edit' | 'saveOrUpdate';
    initialFilterName?: string;
    filterToEditId?: string;
    currentFilterState?: any;
    activeFilterName?: string;
}

// 2. MAIN DIALOG COMPONENT
const SaveFilterDialog: React.FC<SaveFilterDialogProps> = ({
    open,
    onClose,
    onSubmit,
    mode,
    initialFilterName,
    filterToEditId,
    currentFilterState,
    activeFilterName,
}) => {
    // --- State and Logic ---
    const [filterName, setFilterName] = useState('');
    const [saveOption, setSaveOption] = useState<'update' | 'new'>('new');

    useEffect(() => {
        if (open) {
            if (mode === 'edit') {
                setFilterName(initialFilterName || '');
            } else {
                setFilterName('');
            }
            setSaveOption(mode === 'saveOrUpdate' ? 'update' : 'new');
        }
    }, [initialFilterName, open, mode]);

    const handleSubmit = () => {
        if (mode === 'saveOrUpdate') {
            if (saveOption === 'update') {
                onSubmit('UPDATE_EXISTING', filterToEditId);
            } else {
                if (filterName.trim()) {
                    onSubmit(filterName, undefined, currentFilterState);
                }
            }
        } else if (filterName.trim()) {
            if (mode === 'save') {
                onSubmit(filterName, undefined, currentFilterState);
            } else {
                onSubmit(filterName, filterToEditId);
            }
        }
        onClose();
    };

    const dialogTitle = mode === 'edit' ? "Edit Filter Name" : "Save Filter";
    const isSaveDisabled = (mode === 'saveOrUpdate' && saveOption === 'new' && !filterName.trim()) ||
                           (mode !== 'saveOrUpdate' && !filterName.trim());

    // --- JSX Rendering ---
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)',
                    width: '600px',
                    minHeight: '330px',
                }
            }}
        >
            <DialogTitle sx={{ p: 0, borderTopLeftRadius: '12px', borderTopRightRadius: '12px', overflow: 'hidden' }}>
                <Box sx={{ background: `url(/textures/Gradient_Popup.svg) no-repeat right center` }}>
                    <Box sx={{ p: '35px', background: `url(/textures/Texture.svg) no-repeat right center`, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ backgroundColor: 'rgba(229, 239, 255, 1)', borderRadius: '12.8px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px'}}>
                            <img src={mode === 'edit' ? "/icons/edit_icon_card.svg" : "/icons/save_filter.svg"} alt="Dialog Icon" style={{ width: 24, height: 24  }} />
                        </Box>
                        <Box sx={{ ml: 2 }}>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>{dialogTitle}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                                {mode === 'edit' ? `Edit below filled to update the filter name` : "Save filters for quick access later."}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3, pt: 3 }}>
                {mode === 'saveOrUpdate' && (
                    <Box>
                        <RadioGroup
                            row
                            value={saveOption}
                            onChange={(e) => setSaveOption(e.target.value as 'update' | 'new')}
                        >
                            <FormControlLabel
                                value="update"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' }}}/>}
                                label="Update Existing"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px', color: saveOption === 'update' ? 'text.primary' : 'text.secondary', fontWeight: saveOption === 'update' ? 500 : 400 } }}
                            />
                            <FormControlLabel
                                value="new"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' }}}/>}
                                label="Save As New Filter"
                                sx={{ ml: 2, '& .MuiFormControlLabel-label': { fontSize: '13px', color: saveOption === 'new' ? 'text.primary' : 'text.secondary', fontWeight: saveOption === 'new' ? 500 : 400 } }}
                            />
                        </RadioGroup>

                        {saveOption === 'update' && (
                             <Alert
                                severity="info"
                                    icon={<img src="/icons/alert_icon.svg" alt="Information" style={{ width: '20px', height: '20px' }} />}
                                sx={{
                                    mt: 3,
                                    fontSize: '13px',
                                    py: 0.5,
                                    backgroundColor: ' rgba(227, 242, 253, 1)',
                                    color: 'rgba(51, 51, 51, 1)',
                                    borderRadius:'8px',
                                    border:'1px solid rgba(33, 150, 243, 1)'
                                }}
                            >
                                This will update your existing saved filter with the latest changes.
                            </Alert>
                        )}
                    </Box>
                )}

                {(mode === 'save' || mode === 'edit' || (mode === 'saveOrUpdate' && saveOption === 'new')) && (
                    <Box mt={mode === 'saveOrUpdate' ? 4 : 0}>
                        <Typography variant="body2" sx={{ mt: 2,mb: 1, fontWeight: 400, color: '#333' }}>
                            Filter Name <Box component="span" sx={{ color: 'red' }}>*</Box>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter filter name here"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px', backgroundColor: '#FFFFFF',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#0064FF' },
                                    '&.Mui-focused fieldset': { borderColor: '#0064FF', borderWidth: '1px' },
                                },
                                '& .MuiInputBase-input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '14px' },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            
            {/* ================================================================== */}
            {/* === EDITED ACTIONS SECTION === */}
            {/* ================================================================== */}
            <DialogActions sx={{ p: '24px', mt: 'auto' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    disableElevation
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        width: '100px',
                        height: '40px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        '&:hover': {
                            backgroundColor: '#E5E7EB',
                        },
                        mr: 1
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={isSaveDisabled}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        height: '40px',
                        backgroundColor: '#0064FF',
                        '&:hover': { backgroundColor: '#0052CC' },
                        '&.Mui-disabled': {
                            backgroundColor: '#D1D5DB',
                            color: '#9CA3AF'
                        }
                    }}
                >
                    {mode === 'edit' ? "Save Changes" : "Save Filter"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaveFilterDialog;